import React, { useEffect, useState } from "react";
import { Progress, Steps } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { AI_THEME, AiBadge, AiStyles } from "./faceSearchTheme";

const STAGE_STEPS = [
  "Upload",
  "Analyze",
  "Match",
  "Complete",
];

export const FaceSearchAiLoader = ({
  imageUrl,
  stage = "Initializing AI face engine...",
  complete = false,
}) => {
  const [progress, setProgress] = useState(10);
  const activeStep = complete ? 3 : progress > 70 ? 2 : progress > 35 ? 1 : 0;

  useEffect(() => {
    if (complete) {
      setProgress(100);
      return undefined;
    }

    setProgress(10);
    const timer = setInterval(() => {
      setProgress((prev) => (prev >= 94 ? prev : prev + Math.random() * 7 + 2));
    }, 1600);

    return () => clearInterval(timer);
  }, [complete, stage]);

  return (
    <div
      style={{
        ...aiLoaderShell,
        background: AI_THEME.bg,
      }}
    >
      <AiStyles />
      <div style={aiLoaderCard}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <AiBadge label="AI Face Recognition Engine" />
        </div>

        <div style={{ position: "relative", width: 260, margin: "0 auto 28px" }}>
          <div style={pulseRing} />
          <div style={faceFrame}>
            {imageUrl ? (
              <img src={imageUrl} alt="Analyzing" style={faceImage} />
            ) : (
              <div style={facePlaceholder}>Processing</div>
            )}
            {!complete && <div style={scanLine} />}
            <div style={cornerTopLeft} />
            <div style={cornerTopRight} />
            <div style={cornerBottomLeft} />
            <div style={cornerBottomRight} />
          </div>
        </div>

        <div style={stageTitle}>
          {complete ? (
            <>
              <CheckCircleOutlined style={{ color: AI_THEME.success, marginRight: 8 }} />
              Analysis Complete
            </>
          ) : (
            stage
          )}
        </div>
        <div style={stageSubtitle}>
          {complete
            ? "Generating similarity results..."
            : "Neural network is scanning facial features and matching records"}
        </div>

        <Steps
          size="small"
          current={activeStep}
          style={{ margin: "22px 0 18px" }}
          items={STAGE_STEPS.map((title) => ({ title }))}
        />

        <Progress
          percent={Math.round(progress)}
          status={complete ? "success" : "active"}
          strokeColor={AI_THEME.primary}
          trailColor="#e8eef8"
          strokeWidth={10}
        />
      </div>
    </div>
  );
};

const aiLoaderShell = {
  width: "100%",
  minHeight: 560,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "28px 16px",
  borderRadius: AI_THEME.radius,
  border: `1px solid ${AI_THEME.cardBorder}`,
};

const aiLoaderCard = {
  width: "100%",
  maxWidth: 560,
  background: AI_THEME.card,
  borderRadius: AI_THEME.radius,
  padding: "30px 28px 34px",
  border: `1px solid ${AI_THEME.cardBorder}`,
  boxShadow: AI_THEME.shadow,
};

const pulseRing = {
  position: "absolute",
  inset: -16,
  borderRadius: "50%",
  border: `2px solid rgba(47, 84, 235, 0.28)`,
  animation: "aiPulse 2.4s ease-in-out infinite",
};

const faceFrame = {
  position: "relative",
  width: 228,
  height: 228,
  margin: "0 auto",
  borderRadius: "50%",
  overflow: "hidden",
  border: `3px solid ${AI_THEME.primary}`,
  background: "#0d1b33",
  animation: "aiGlow 2.6s ease-in-out infinite",
};

const faceImage = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  opacity: 0.94,
};

const facePlaceholder = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#8c8c8c",
  fontSize: 13,
};

const scanLine = {
  position: "absolute",
  left: 0,
  right: 0,
  height: 3,
  background: `linear-gradient(90deg, transparent, ${AI_THEME.accent}, ${AI_THEME.primary}, ${AI_THEME.accent}, transparent)`,
  animation: "aiScan 2.1s ease-in-out infinite",
  zIndex: 2,
};

const cornerBase = {
  position: "absolute",
  width: 18,
  height: 18,
  borderColor: AI_THEME.accent,
  zIndex: 3,
};

const cornerTopLeft = { ...cornerBase, top: 14, left: 14, borderTop: "2px solid", borderLeft: "2px solid" };
const cornerTopRight = { ...cornerBase, top: 14, right: 14, borderTop: "2px solid", borderRight: "2px solid" };
const cornerBottomLeft = { ...cornerBase, bottom: 14, left: 14, borderBottom: "2px solid", borderLeft: "2px solid" };
const cornerBottomRight = { ...cornerBase, bottom: 14, right: 14, borderBottom: "2px solid", borderRight: "2px solid" };

const stageTitle = {
  textAlign: "center",
  color: AI_THEME.primary,
  fontSize: 17,
  fontWeight: 700,
  marginBottom: 8,
  minHeight: 26,
};

const stageSubtitle = {
  textAlign: "center",
  color: AI_THEME.muted,
  fontSize: 13,
  lineHeight: 1.5,
  marginBottom: 4,
};
